/**
 * Home's pure selectors: which of the organization's chats are recent or
 * waiting on you, what is coming up, and the words the page says about
 * them. Everything here reads real stores (thread shells, policy
 * governance, regulatory updates) and
 * is scoped to one organization by the caller's `orgId`; nothing is sampled.
 *
 * @module components/home/homeLogic
 */
import type { EnvironmentThreadShell } from "@amoreg/client-runtime/state/models";

import type { OrgPolicyGovernance } from "../../state/governance";
import type { RegulatoryUpdate } from "../../state/policies";
import { resolveSidebarThreadStatus } from "../Sidebar.logic";

/** NaN-safe: a malformed timestamp sinks to the epoch instead of poisoning a sort. */
const timestampMs = (iso: string | null | undefined): number => {
  const parsed = iso ? Date.parse(iso) : Number.NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** The last time anyone did anything in a chat: a message, a reply, a settle. */
export function threadActivityMs(
  thread: Pick<EnvironmentThreadShell, "updatedAt" | "latestUserMessageAt">,
): number {
  return Math.max(timestampMs(thread.updatedAt), timestampMs(thread.latestUserMessageAt));
}

/**
 * The organization's chats, and only those: the org's workspace is the
 * project whose id is the org id (the mirror invariant), on the primary
 * environment. No org or environment means no chats, never every chat.
 */
export function selectOrgThreads<
  T extends Pick<EnvironmentThreadShell, "archivedAt" | "projectId" | "environmentId">,
>(
  threads: ReadonlyArray<T>,
  scope: { readonly orgId: string | null; readonly environmentId: string | null },
): T[] {
  const { orgId, environmentId } = scope;
  if (orgId === null || environmentId === null) return [];
  return threads.filter(
    (thread) =>
      thread.archivedAt === null &&
      thread.projectId === orgId &&
      thread.environmentId === environmentId,
  );
}

/** What a chat's row says on Home: the agent is working, or it waits on you. */
export type HomeThreadState = "working" | "waiting" | null;

export function homeThreadState(
  thread: Parameters<typeof resolveSidebarThreadStatus>[0],
): HomeThreadState {
  const status = resolveSidebarThreadStatus(thread);
  if (status === "approval" || status === "input") return "waiting";
  if (status === "working" || status === "monitoring") return "working";
  return null;
}

export type HomeWaitingKind = "approval" | "input";

export interface HomeWaitingChat<T> {
  readonly thread: T;
  readonly kind: HomeWaitingKind;
  /** The button on its row: an approval is reviewed, a question answered. */
  readonly action: "Review" | "Answer";
}

/** Chats stopped on you, newest first: a pending approval or an unanswered question. */
export function selectWaitingChats<T extends EnvironmentThreadShell>(
  threads: ReadonlyArray<T>,
): HomeWaitingChat<T>[] {
  return threads
    .filter((thread) => thread.hasPendingApprovals || thread.hasPendingUserInput)
    .toSorted((left, right) => threadActivityMs(right) - threadActivityMs(left))
    .map((thread) =>
      thread.hasPendingApprovals
        ? { thread, kind: "approval" as const, action: "Review" as const }
        : { thread, kind: "input" as const, action: "Answer" as const },
    );
}

export type HomeComingUpKind = "review" | "effective" | "comments";

export interface HomeComingUpItem {
  readonly id: string;
  readonly kind: HomeComingUpKind;
  readonly title: string;
  /** "Policy review", "Takes effect", "Comments close". */
  readonly detail: string;
  /** The chip beside it: the policy a review is for, the regime a publication is from. */
  readonly context: string | null;
  readonly at: string;
  readonly overdue: boolean;
  /** The policy a review belongs to; null for a publication. */
  readonly policyId: string | null;
  /** The publication an effective date or comment deadline belongs to; null for a review. */
  readonly updateId: string | null;
}

/** A review is named by its document; a publication's date by the publication itself, not its regime. */
export const comingUpName = (item: Pick<HomeComingUpItem, "kind" | "title" | "context">) =>
  item.kind === "review" ? (item.context ?? item.title) : item.title;

const REVIEW_TITLE: Record<OrgPolicyGovernance["reviewFrequency"], string> = {
  none: "Policy review",
  monthly: "Monthly review",
  quarterly: "Quarterly review",
  semiannual: "Half-yearly review",
  yearly: "Annual review",
};

const COMING_UP_DETAIL: Record<HomeComingUpKind, string> = {
  review: "Policy review",
  effective: "Takes effect",
  comments: "Comments close",
};

/**
 * Dated work from real sources, soonest first: policy reviews (overdue ones
 * included, since they are the most pressing), and the effective dates and
 * comment deadlines of publications not ruled out as not applicable. Past
 * publication dates are history, not "coming up", and are left out.
 */
export function selectComingUp(input: {
  readonly policies: ReadonlyArray<OrgPolicyGovernance>;
  readonly updates: ReadonlyArray<RegulatoryUpdate>;
  readonly now: number;
  /** Omit for every dated item. */
  readonly limit?: number;
}): HomeComingUpItem[] {
  const items: HomeComingUpItem[] = [];
  for (const policy of input.policies) {
    if (policy.lifecycleStatus === "archived" || policy.reviewDueAt === null) continue;
    const at = Date.parse(policy.reviewDueAt);
    if (Number.isNaN(at)) continue;
    items.push({
      id: `review:${policy.policyId}`,
      kind: "review",
      title: REVIEW_TITLE[policy.reviewFrequency],
      detail: COMING_UP_DETAIL.review,
      context: policy.title?.trim() || "Untitled policy",
      at: policy.reviewDueAt,
      overdue: at < input.now,
      policyId: policy.policyId,
      updateId: null,
    });
  }
  for (const update of input.updates) {
    if (update.applicability === "not_applicable") continue;
    const dates: ReadonlyArray<readonly [HomeComingUpKind, string | null | undefined]> = [
      ["effective", update.effectiveAt],
      ["comments", update.commentDeadlineAt],
    ];
    for (const [kind, iso] of dates) {
      if (!iso) continue;
      const at = Date.parse(iso);
      if (Number.isNaN(at) || at < input.now) continue;
      items.push({
        id: `${kind}:${update.id}`,
        kind,
        title: update.title,
        detail: COMING_UP_DETAIL[kind],
        context: update.frameworkName?.trim() || update.jurisdictionName || null,
        at: iso,
        overdue: false,
        policyId: null,
        updateId: update.id,
      });
    }
  }
  return items
    .toSorted((left, right) => Date.parse(left.at) - Date.parse(right.at) || left.id.localeCompare(right.id))
    .slice(0, input.limit ?? Number.POSITIVE_INFINITY);
}

export function greetingFor(hour: number): string {
  return hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
}

/**
 * The name Home greets: the first name, else the first word of the full
 * name, else the email's first part ("dev@flawstick.com" → "Dev",
 * "ann.lee@…" → "Ann"); null only with nothing to go on.
 */
export function greetingName(account: {
  readonly firstName?: string | null | undefined;
  readonly lastName?: string | null | undefined;
  readonly email?: string | undefined;
  readonly subject?: string | undefined;
}): string | null {
  const first = account.firstName?.trim();
  if (first) return first.split(/\s+/)[0]!;
  const last = account.lastName?.trim();
  if (last) return last.split(/\s+/)[0]!;
  const email = account.email ?? (account.subject?.startsWith("workos:") ? account.subject.slice(7) : account.subject);
  const local = email?.includes("@") ? email.split("@")[0]!.split("+")[0]! : null;
  const word = local?.split(/[._-]+/).find(Boolean);
  return word ? word[0]!.toUpperCase() + word.slice(1).toLowerCase() : null;
}

/** A draft Home prepared, with the organization (environment and project) it was opened for. */
export interface HomeScopedDraft<Id> {
  readonly id: Id;
  readonly scope: string;
}

export const homeDraftScope = (environmentId: string, projectId: string): string => `${environmentId}:${projectId}`;

/** The draft to show for this organization: the prepared one only when it was opened for it. */
export function draftForScope<Id>(draft: HomeScopedDraft<Id> | null, scope: string): Id | null {
  return draft !== null && draft.scope === scope ? draft.id : null;
}

/** "now", "5m", "3h", "2d": the compact age the sidebar rows already use. */
export function compactAge(iso: string, now: number): string {
  const minutes = Math.max(0, Math.round((now - timestampMs(iso)) / 60_000));
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days < 14) return `${days}d`;
  if (days < 60) return `${Math.round(days / 7)}w`;
  return `${Math.round(days / 30)}mo`;
}
